import { useEffect, useReducer } from 'react';
import { SWRResponse } from 'swr';
import { ListResponse } from '@tlx/astro-shared';
import { EmployeeDTO } from '../SalaryTaxCard.types';
import {
    AddEmployeeState,
    SelectAllState,
    addEmployeesReducer,
    stateContainsAllEmployees,
    stateContainsEmployee,
    stateContainsNoEmployees,
    stateContainsSomeButNotAllEmployees,
    stateSelectAllState,
} from '../reducers/taxcardSelectAllReducer';

export interface TaxcardSelectableMachinery {
    state: AddEmployeeState;
    allChecked: boolean;
    someChecked: boolean;
    selectAllState: SelectAllState;
    isRowChecked: (employeeId: number) => boolean;
    onCheckAll: () => void;
    onCheckRow: (employeeId: number) => void;
    onUncheckAll: () => void;
}

export const useTaxcardSelectableEmployees = (
    selectable: SWRResponse<ListResponse<EmployeeDTO>>,
): TaxcardSelectableMachinery => {
    const [state, updateState] = useReducer(addEmployeesReducer, {
        selectedEmployees: [],
        selectableEmployees: [],
    });

    useEffect(() => {
        if (!selectable.isLoading) {
            updateState({ type: 'SelectAllLoading', value: false });
            if (selectable.data) {
                updateState({
                    type: 'SetSelectableEmployees',
                    employeeIds: selectable.data.values.map((e) => e.id),
                });
            }
            //you have previously clicked selectAll, but it wasn't able to as it was loading
            if (state.clickedButSelectAllWasLoading) {
                updateState({ type: 'SelectAll' });
            }
        } else {
            updateState({ type: 'SelectAllLoading', value: true });
        }
    }, [selectable.isLoading, selectable.data]);

    const checkAllHandler = () => {
        if (stateContainsNoEmployees(state)) {
            updateState({
                type: 'SelectAll',
            });
        } else {
            updateState({
                type: 'UncheckAllEmployees',
            });
        }
    };

    const checkRowHandler = (employeeId: number) => {
        if (stateContainsEmployee(state, employeeId)) {
            updateState({
                type: 'UncheckSingleEmployee',
                employeeId,
            });
        } else {
            updateState({
                type: 'CheckSingleEmployee',
                employeeId,
            });
        }
    };

    const rowChecked = (employeeId: number) => stateContainsEmployee(state, employeeId);

    const uncheckAllHandler = () => {
        updateState({
            type: 'UncheckAllEmployees',
        });
    };

    return {
        state,
        allChecked: stateContainsAllEmployees(state),
        someChecked: stateContainsSomeButNotAllEmployees(state),
        selectAllState: stateSelectAllState(state),
        isRowChecked: rowChecked,
        onCheckAll: checkAllHandler,
        onCheckRow: checkRowHandler,
        onUncheckAll: uncheckAllHandler,
    };
};
